import { CheckIcon, CircleNotchIcon } from '@phosphor-icons/react';
import { ChevronDown } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useGetTestId } from '@/hooks';
import { cn } from '@/lib/utils';

import { OptionType } from '../types';

export type AutocompleteProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  options: OptionType[];
  label?: string;
  placeholder?: string;
  onChange?: (id: string) => void;
  isDisabled?: boolean;
  searchable?: boolean;
  className?: string;
  iconColor?: string;
  defaultValue?: string;
  isLoading?: boolean;
  requiredMark?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
};

export const Autocomplete = <T extends FieldValues>({
  name,
  control,
  label,
  options = [],
  onChange,
  isDisabled,
  searchable,
  className,
  defaultValue = '',
  iconColor = colors.zinc[200],
  placeholder = 'Select option',
  isLoading,
  requiredMark,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: AutocompleteProps<T>) => {
  const [open, setOpen] = useState(false);
  const testId = useGetTestId(name);
  const parentRef = useRef<HTMLButtonElement>(null);
  const [searchValue, setSearchValue] = useState('');

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;
      const el = e.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
        onLoadMore();
      }
    },
    [onLoadMore, hasNextPage, isFetchingNextPage]
  );

  const filteredOptions = useMemo(() => {
    if (!searchValue) return options;
    return options.filter((option) =>
      option.value.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  const getSelectValue = useCallback(
    (formValue: string) => {
      if (defaultValue) {
        return defaultValue;
      }
      return formValue
        ? options.find((option) => option.id === formValue)?.value
        : placeholder;
    },
    [options, placeholder, defaultValue]
  );

  const disabled = isDisabled || isLoading;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, formState: { errors } }) => {
        const value = getSelectValue(field.value);

        return (
          <FormItem>
            <FormControl>
              <div className="grid gap-2">
                {label && (
                  <FormLabel
                    htmlFor={name}
                    className={cn(
                      'text-sm font-medium',
                      disabled && 'pointer-events-none'
                    )}
                  >
                    {label}
                    {requiredMark && <span className="text-red-500">*</span>}
                  </FormLabel>
                )}
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      id={name}
                      ref={parentRef}
                      variant="outline"
                      size="field"
                      data-testid={testId}
                      className={cn(
                        'w-full justify-between font-normal mt-1 hover:opacity-100 overflow-hidden',
                        value === placeholder &&
                          'text-muted-foreground hover:text-muted-foreground',
                        disabled && 'cursor-not-allowed bg-zinc-700',
                        className,
                        // Error border
                        !!errors[name]
                          ? 'border-destructive focus-visible:ring-500'
                          : 'border-input'
                      )}
                      disabled={disabled}
                    >
                      <p className="text-ellipsis whitespace-nowrap overflow-hidden">
                        {value}
                      </p>
                      {isLoading ? (
                        <CircleNotchIcon size={24} className="animate-spin" />
                      ) : (
                        <ChevronDown
                          color={iconColor}
                          className="h-4 ml-2 cursor-pointer text-muted-foreground"
                        />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent
                      className="p-0"
                      align="start"
                      sideOffset={5}
                      style={{ width: parentRef.current?.offsetWidth }}
                    >
                      <Command shouldFilter={false}>
                        {searchable && (
                          <CommandInput
                            onValueChange={setSearchValue}
                            placeholder="Search..."
                            data-testid="autocomplete-search-input"
                          />
                        )}
                        <CommandList className="max-h-60 overflow-y-auto" onScroll={handleListScroll}>
                          <CommandEmpty>No option found.</CommandEmpty>
                          <CommandGroup data-testid="autocomplete-list">
                            {filteredOptions.map(({ value, id }) => (
                              <CommandItem
                                key={id}
                                value={id}
                                data-testid="autocomplete-list-item"
                                onSelect={() => {
                                  field.onChange(field.value === id ? '' : id);
                                  onChange?.(field.value === id ? '' : id);
                                  setOpen(false);
                                }}
                              >
                                {value}
                                <CheckIcon
                                  className={cn(
                                    'ml-auto',
                                    id === field.value
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {isFetchingNextPage && (
                            <div className="flex justify-center py-2">
                              <CircleNotchIcon size={16} className="animate-spin text-zinc-400" />
                            </div>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </PopoverPortal>
                </Popover>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
